import React, { forwardRef, type HTMLAttributes, useCallback, useContext, useRef } from 'react';

import { SCROLLBAR_WIDTH, TOOLBAR_HEIGHT } from '../constants';
import { LightboxContext } from '../LightboxContext';

export const BackdropContainer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  (props, ref) => {
    const { close } = useContext(LightboxContext);

    // We have to listen to mousedown and mouseup instead of click because we need to make sure that
    // the mousedown target is the container and not one of its children. When using click, target
    // will be the target of the mouseup event, which causes unintentional firing when dragging
    // images.
    const isLastMouseDownValidRef = useRef(false);

    const onMouseUp = useCallback(() => {
      if (!isLastMouseDownValidRef.current) return;

      close();
    }, [close]);

    const onMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const isTargetingVerticalScrollbar =
        event.currentTarget.offsetWidth - event.pageX < SCROLLBAR_WIDTH;
      const isTargetingHorizontalScrollbar =
        TOOLBAR_HEIGHT + event.currentTarget.offsetHeight - event.pageY < SCROLLBAR_WIDTH;

      isLastMouseDownValidRef.current =
        event.target === event.currentTarget &&
        !isTargetingVerticalScrollbar &&
        !isTargetingHorizontalScrollbar;
    }, []);

    return (
      <div
        onMouseUp={onMouseUp}
        onMouseDown={onMouseDown}
        ref={ref}
        role="button"
        tabIndex={-1}
        {...props}
      />
    );
  },
);

BackdropContainer.displayName = 'BackdropContainer';
